import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, Copy, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { clearActiveCall, getActiveCall, onCallChange } from "@/lib/callState";

/**
 * Floating call banner — visible on any page as long as there is an
 * active call in localStorage. Shows a live timer, a "copiar nº"
 * button and "Terminei a chamada" that ends the backend segment and
 * dismisses the banner.
 *
 * Rendered once at the App root so it persists across route changes.
 * Requirements confirmed by user 04/08/2026:
 * - Floating notification bottom-right, global (persists across pages)
 * - Timer + botão terminar + botão "copiar nº novamente"
 * - Removes the old "Iniciar chamada" button from LeadDetail (handled
 *   in CallControls via ``showStart={false}``).
 */
export default function CallBanner() {
  const [call, setCall] = useState(() => getActiveCall());
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  // Subscribe to storage/custom events so the banner tracks
  // start/end/change events across the whole app.
  useEffect(() => onCallChange(setCall), []);

  // Cronómetro live enquanto há chamada.
  useEffect(() => {
    if (!call) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [call]);

  if (!call) return null;

  const started = new Date(call.startedAt).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - started) / 1000));
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  const fmt = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  void tick; // keeps ESLint happy — we intentionally use ``now`` from the render itself

  const copyPhone = async () => {
    if (!call.phone) return;
    try {
      await navigator.clipboard.writeText(call.phone);
      toast.success(`Número ${call.phone} copiado`);
    } catch {
      window.prompt("Copia o número:", call.phone);
    }
  };

  const endCall = async () => {
    if (!call.leadId) return;
    setBusy(true);
    try {
      const r = await api.post(`/leads/${call.leadId}/call/end`, { outcome: "success" });
      const secs = Math.round(r.data?.duration_sec || seconds);
      toast.success(`Chamada terminada · ${secs}s registados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a terminar chamada");
    } finally {
      clearActiveCall();
      setBusy(false);
    }
  };

  // Amber tint after 8 minutes; red pulse after 10 (mirrors the
  // pre-call modal thresholds so the operator learns one visual
  // language for "time to wrap up").
  const isWarning = seconds >= 8 * 60 && seconds < 10 * 60;
  const isOverLimit = seconds >= 10 * 60;

  return (
    <div
      className="fixed bottom-5 right-5 z-[70] w-[340px] max-w-[92vw]"
      data-testid="call-banner"
    >
      <div className={`bg-card rounded-2xl shadow-2xl border overflow-hidden ${
        isOverLimit ? "border-[hsl(var(--danger))]/40 ring-2 ring-rose-200"
        : isWarning ? "border-[hsl(var(--warning))]/40 ring-2 ring-amber-100"
        : "border-border"
      }`}>
        {/* Header — status pill + close cross closes only the panel via
            end call ». */}
        <div className={`px-4 py-3 flex items-center gap-3 ${
          isOverLimit ? "bg-[hsl(var(--danger-bg))]"
          : isWarning ? "bg-[hsl(var(--warning-bg))]"
          : "bg-[hsl(var(--success-bg))]"
        }`}>
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            isOverLimit ? "bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/40"
            : isWarning ? "bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40"
            : "bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40"
          }`}>
            <PhoneCall className={`w-4 h-4 ${
              isOverLimit ? "text-[hsl(var(--danger))] animate-pulse"
              : isWarning ? "text-[hsl(var(--warning))]"
              : "text-[hsl(var(--success))]"
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">
              Chamada em curso
            </div>
            <div className="text-sm text-foreground font-heading font-semibold truncate" data-testid="call-banner-name">
              {call.leadName || "—"}
            </div>
          </div>
          <div
            className={`font-mono text-lg tabular-nums ${
              isOverLimit ? "text-[hsl(var(--danger))]" : isWarning ? "text-[hsl(var(--warning))]" : "text-foreground"
            }`}
            data-testid="call-banner-timer"
          >
            {fmt}
          </div>
        </div>

        {isOverLimit && (
          <div className="px-4 py-1.5 bg-rose-500 text-primary-foreground text-[11px] font-heading text-center">
            Já vai em {fmt} — deve terminar a chamada
          </div>
        )}

        <div className="px-4 py-3 border-t border-border/60 flex items-center gap-2">
          <button
            onClick={copyPhone}
            disabled={!call.phone}
            data-testid="call-banner-copy"
            className="inline-flex items-center gap-1.5 text-[11px] font-heading text-foreground bg-card hover:bg-muted/40 border border-border rounded-full px-3 py-1.5 disabled:opacity-40"
            title={call.phone ? `Copiar ${call.phone}` : "Sem telefone"}
          >
            <Copy className="w-3 h-3" /> Copiar nº
          </button>
          <button
            onClick={endCall}
            disabled={busy}
            data-testid="call-banner-end"
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-heading text-primary-foreground bg-rose-600 hover:bg-rose-700 rounded-full px-3 py-1.5 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Terminei a chamada
          </button>
        </div>
      </div>
    </div>
  );
}
